import * as d3 from 'd3';
import { addYears, format, parseISO } from 'date-fns';
import { useMemo } from 'react';

import { sampleDataToWeekly } from './sampleDataToWeekly';

export interface TargetPriceChartProps {
  /** Historical price points (date string + price). Sampled to roughly weekly intervals before plotting. */
  timeseries: Array<{ date: string; price: number }>;
  /** Analyst target prices: high / average / low — drawn as horizontal levels on the right half of the chart. */
  targetPrice: { high: number; average: number; low: number };
}

export const useChartData = (params: {
  timeseries: TargetPriceChartProps['timeseries'];
  targetPrice: TargetPriceChartProps['targetPrice'];
  leftChartWidth: number;
  rightChartWidth: number;
  chartHeight: number;
}) => {
  const {
    timeseries,
    targetPrice,
    leftChartWidth,
    rightChartWidth,
    chartHeight,
  } = params;

  return useMemo(() => {
    if (!timeseries.length) {
      return { scales: null, paths: null, axisData: null };
    }

    // Sample data to roughly weekly intervals based on actual time span
    const sampledTimeseries = sampleDataToWeekly(timeseries);

    // Parse dates and get data ranges
    const parsedData = sampledTimeseries.map(d => ({
      date: parseISO(d.date),
      price: d.price,
    }));

    const firstPrice = parsedData[0]?.price || 0;
    const lastPrice = parsedData[parsedData.length - 1]?.price || 0;
    const isBullish = lastPrice >= firstPrice;

    // Determine if target prices are bearish compared to starting price
    const isTargetHighBearish = targetPrice.high < lastPrice;
    const isTargetAvgBearish = targetPrice.average < lastPrice;
    const isTargetLowBearish = targetPrice.low < lastPrice;
    const allPrices = [
      ...timeseries.map(d => d.price),
      targetPrice.high,
      targetPrice.low,
    ];
    const priceExtent = d3.extent(allPrices) as [number, number];
    const chartWidth = leftChartWidth + rightChartWidth;

    // Create Y scale
    const yScale = d3.scaleLinear().domain(priceExtent).range([chartHeight, 0]);

    // Generate Y-axis labels (4 labels)
    const yAxisTicks = yScale.ticks(4);
    const yAxisData = yAxisTicks.map(tick => ({
      value: tick,
      y: yScale(tick),
      label: `${tick.toFixed(0)}`,
    }));

    // Generate X-axis labels with consistent year timeline
    const formatMonthYear = (date: Date) => {
      return format(date, "MMM''yy");
    };

    const lastDate =
      parsedData[parsedData.length - 1]?.date ||
      parseISO(
        timeseries[timeseries.length - 1]?.date || new Date().toISOString()
      );

    // Create a fixed 12-month timeline ending at the last data point
    const timelineStart = addYears(lastDate, -1);
    const timelineEnd = lastDate;
    const futureDate = addYears(lastDate, 1);

    // Update X scale to use the fixed timeline (always 12 months)
    const fixedXScale = d3
      .scaleTime()
      .domain([timelineStart, timelineEnd])
      .range([0, leftChartWidth]);

    // Filter data to only include points within the timeline range
    const timelineData = parsedData.filter(
      d => d.date >= timelineStart && d.date <= timelineEnd
    );

    // Regenerate time series path with fixed scale and filtered data
    const fixedLine = d3
      .line<{ date: Date; price: number }>()
      .x(d => fixedXScale(d.date))
      .y(d => yScale(d.price))
      .curve(d3.curveMonotoneX);

    const fixedTimeseriesPath = fixedLine(timelineData) || '';

    // Update target paths to use fixed scale
    const lastTimelineDataPoint =
      timelineData[timelineData.length - 1] ||
      parsedData[parsedData.length - 1];
    const fixedLastX = fixedXScale(lastTimelineDataPoint.date);
    const targetX = leftChartWidth + rightChartWidth - 20;

    const fixedTargetHighPath = `M${fixedLastX},${yScale(
      lastTimelineDataPoint.price
    )} L${targetX},${yScale(targetPrice.high)}`;
    const fixedTargetAvgPath = `M${fixedLastX},${yScale(
      lastTimelineDataPoint.price
    )} L${targetX},${yScale(targetPrice.average)}`;
    const fixedTargetLowPath = `M${fixedLastX},${yScale(
      lastTimelineDataPoint.price
    )} L${targetX},${yScale(targetPrice.low)}`;

    const xAxisData = [
      { date: timelineStart, x: 0, label: formatMonthYear(timelineStart) },
      {
        date: timelineEnd,
        x: leftChartWidth,
        label: formatMonthYear(timelineEnd),
      },
      { date: futureDate, x: chartWidth, label: formatMonthYear(futureDate) },
    ];

    return {
      scales: { xScale: fixedXScale, yScale },
      paths: {
        timeseries: fixedTimeseriesPath,
        targetHigh: fixedTargetHighPath,
        targetAvg: fixedTargetAvgPath,
        targetLow: fixedTargetLowPath,
      },
      axisData: { yAxis: yAxisData, xAxis: xAxisData },
      isBullish,
      targetBearish: {
        high: isTargetHighBearish,
        average: isTargetAvgBearish,
        low: isTargetLowBearish,
      },
    };
  }, [timeseries, targetPrice, leftChartWidth, rightChartWidth, chartHeight]);
};
